<!-- Read — turn a friend's footprints back into words.

     Reading is JUST reading: no save, no keep, nothing downstream. Find each
     print on the key, tap it, and the word grows in the band. The band talks
     when it is empty so the first tap is never a leap of faith. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" class="decode">
      <div class="sheet">
        <header class="head">
          <h1>Read footprints</h1>
          <p>Got a note written in footprints? Find each print below and tap it — the words appear as you go.</p>
        </header>

        <div class="reading" :class="{ empty: !picked.length }" aria-live="polite">
          {{ picked.length ? picked.join('') : 'The words appear here' }}
        </div>

        <div class="grid">
          <button
            v-for="g in cells" :key="g.character" type="button" class="cell"
            :aria-label="`The letter ${g.character}`" @click="picked.push(g.character)"
          >
            <svg viewBox="-24 -26 48 52" aria-hidden="true">
              <path v-for="(p, i) in g.prints" :key="i" :d="p.d" :transform="p.transform" fill="#6b4a2f" />
            </svg>
          </button>
        </div>

        <div class="actions">
          <button type="button" @click="picked.push(' ')">Space</button>
          <button type="button" @click="picked.pop()">Undo</button>
          <button type="button" @click="picked.splice(0)">Clear</button>
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { CHARACTERS, TABLE, printsFor, ensureAlphabet } from '../alphabet.js';

const picked = ref([]);
onMounted(() => ensureAlphabet());

const cells = computed(() => CHARACTERS.split('').map((ch) => ({
  character: ch,
  prints: printsFor(TABLE[ch]),
})));
</script>

<style scoped>
.decode { --background: var(--fs-paper); }
.sheet {
  min-height: 100%; background: var(--fs-paper); color: var(--fs-ink);
  font-family: var(--fs-body);
  padding: calc(14px + env(safe-area-inset-top, 0px)) 0 calc(14px + env(safe-area-inset-bottom, 0px));
}
.head { padding: 0 20px; }
.head h1 { margin: 0; font-family: var(--fs-serif); font-style: italic; font-weight: 400; font-size: 28px; }
.head p { margin: 6px 0 0; font-size: 15px; line-height: 1.45; color: color-mix(in srgb, var(--fs-ink) 72%, transparent); }
.reading {
  margin: 12px 16px 0; padding: 10px 14px; min-height: 30px;
  background: var(--fs-sepia); color: var(--fs-cream);
  font-family: var(--fs-serif); font-size: 24px; letter-spacing: 0.08em;
}
.reading.empty { color: color-mix(in srgb, var(--fs-cream) 55%, transparent); font-style: italic; font-size: 17px; }
.grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 6px; padding: 12px 16px 0; }
.cell {
  display: flex; align-items: center; justify-content: center;
  min-height: 48px; padding: 0;
  background: color-mix(in srgb, white 34%, transparent);
  border: 1px solid color-mix(in srgb, var(--fs-sepia) 22%, transparent); cursor: pointer;
}
.cell:active { background: color-mix(in srgb, var(--fs-sepia) 18%, transparent); }
.cell svg { width: 32px; height: 38px; }
.actions { display: flex; gap: 10px; padding: 12px 20px 0; }
.actions button {
  flex-grow: 1; min-height: 48px; font-family: var(--fs-body); font-size: 16px;
  border: 1px solid color-mix(in srgb, var(--fs-sepia) 55%, transparent); border-radius: 2px;
  background: transparent; color: var(--fs-ink); cursor: pointer;
}
</style>
